import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './TrustStatsCounter.css';

interface StatItem {
  id: string;
  targetNumber: number;
  suffix: string;
  prefix?: string;
  isDecimal?: boolean;
  label: string;
  subLabel: string;
  icon: React.ReactNode;
  badge?: string;
}

const statsData: StatItem[] = [
  {
    id: 'cars-delivered',
    targetNumber: 5000,
    suffix: '+',
    label: 'Vehicles Delivered',
    subLabel: 'Commercial T-Permit & Private Cars',
    badge: 'Maharashtra Milestone',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.5 2.8C2.1 10.7 2 10.8 2 11v5c0 .6.4 1 1 1h2"></path>
        <circle cx="7" cy="17" r="2"></circle>
        <path d="M9 17h6"></path>
        <circle cx="17" cy="17" r="2"></circle>
      </svg>
    )
  },
  {
    id: 'on-road-funding',
    targetNumber: 100,
    suffix: '%',
    label: 'On-Road Funding',
    subLabel: 'Lowest Down Payment & Easy Terms',
    badge: 'SBI & Nationalised Banks',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="5" width="20" height="14" rx="2"></rect>
        <line x1="2" y1="10" x2="22" y2="10"></line>
        <line x1="6" y1="15" x2="6.01" y2="15"></line>
        <line x1="10" y1="15" x2="14" y2="15"></line>
      </svg>
    )
  },
  {
    id: 'fast-sanction',
    targetNumber: 24,
    suffix: ' Hrs',
    label: 'Fast Loan Sanction',
    subLabel: 'Quick Documentation & Approval',
    badge: 'Super Fast Process',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
        <path d="M16 2v4"></path>
        <path d="M8 2v4"></path>
      </svg>
    )
  },
  {
    id: 'customer-rating',
    targetNumber: 4.9,
    suffix: ' / 5',
    isDecimal: true,
    label: 'Customer Trust Rating',
    subLabel: 'Verified Google & Showroom Reviews',
    badge: 'Top Rated Dealership',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
      </svg>
    )
  }
];

const TrustStatsCounter: React.FC = () => {
  const [counts, setCounts] = useState<{ [key: string]: number }>({
    'cars-delivered': 0,
    'on-road-funding': 0,
    'fast-sanction': 0,
    'customer-rating': 0
  });
  const [hasAnimated, setHasAnimated] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);

          // Animate each counter smoothly
          const duration = 2000; // 2 seconds
          const startTime = performance.now();

          const updateCounters = (currentTime: number) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            // Ease out quad
            const easeOutProgress = 1 - Math.pow(1 - progress, 3);

            const newCounts: { [key: string]: number } = {};
            statsData.forEach((stat) => {
              const currentVal = stat.targetNumber * easeOutProgress;
              newCounts[stat.id] = stat.isDecimal
                ? Math.round(currentVal * 10) / 10
                : Math.floor(currentVal);
            });

            setCounts(newCounts);

            if (progress < 1) {
              requestAnimationFrame(updateCounters);
            } else {
              // Ensure exact target numbers at end
              const finalCounts: { [key: string]: number } = {};
              statsData.forEach((stat) => {
                finalCounts[stat.id] = stat.targetNumber;
              });
              setCounts(finalCounts);
            }
          };

          requestAnimationFrame(updateCounters);
        }
      },
      { threshold: 0.25 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  return (
    <section className="asw-trust-section" ref={containerRef}>
      <div className="asw-trust-container">
        {/* Header Badge & Title */}
        <div className="asw-trust-header">
          <div className="asw-trust-pill">
            <span className="asw-trust-pulse-dot"></span>
            Why Maharashtra Chooses ASW Cars
          </div>
          <h2 className="asw-trust-title">
            Your Most Trusted Car Dealership & Finance Partner
          </h2>
          <p className="asw-trust-subtitle">
            Leading the commercial tourist cab & private passenger vehicle revolution with 100% on-road funding, lowest interest rates, and seamless delivery.
          </p>
        </div>

        {/* 4 Counter Cards Grid */}
        <div className="asw-trust-grid">
          {statsData.map((stat) => {
            const currentCount = counts[stat.id] || 0;
            const formattedCount = stat.isDecimal
              ? currentCount.toFixed(1)
              : currentCount.toLocaleString('en-IN');

            return (
              <div key={stat.id} className="asw-stat-card">
                <div className="asw-stat-top">
                  <div className="asw-stat-icon-wrap">
                    {stat.icon}
                  </div>
                  {stat.badge && (
                    <span className="asw-stat-badge">{stat.badge}</span>
                  )}
                </div>

                <div className="asw-stat-number-wrap">
                  <span className="asw-stat-number">{formattedCount}</span>
                  <span className="asw-stat-suffix">{stat.suffix}</span>
                </div>

                <h3 className="asw-stat-label">{stat.label}</h3>
                <p className="asw-stat-sublabel">{stat.subLabel}</p>

                <div className="asw-stat-bar">
                  <div className="asw-stat-bar-fill"></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Trust Strip Banner */}
        <div className="asw-trust-banner">
          <div className="asw-trust-banner-content">
            <div className="asw-trust-banner-text">
              <h4>Ready to get your new car with hassle-free loan approval?</h4>
              <p>Talk to our vehicle finance specialists today for customized EMI options.</p>
            </div>
            <div className="asw-trust-banner-actions">
              <a 
                href="https://wa.me/919987828417?text=Hi%20ASW%20Cars,%20I%20want%20to%20know%20about%20Car%20Finance%20and%20offers." 
                target="_blank" 
                rel="noopener noreferrer"
                className="asw-trust-btn whatsapp-btn"
              >
                <span>WhatsApp Expert</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14"></path>
                  <path d="m12 5 7 7-7 7"></path>
                </svg>
              </a>
              <Link to="/finance-offers" className="asw-trust-btn explore-btn">
                <span>View Bank Offers</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustStatsCounter;
